// Wrapper boot script. Runs in the top-level window of
// `wrapper.html`. Order:
//
//   1. Read edge-injected meta tags (`p2claw-peer-id`, `-alias`,
//      `-binding-sig`, `-binding-issued-at`) and verify the
//      coord-signed alias→peer_id binding against the build-time
//      pinned root pubkey. A compromised edge can rewrite the meta
//      tags; this signature is the one thing it can't forge.
//   2. POST /v1/connect; consistency-check the returned peer_id
//      against the binding (trust anchor; catches coord/edge drift).
//   3. WebRTC handshake — signaling WS + SDP + DTLS-fp verify.
//      Single ordered data channel.
//   4. On connected: register the SW, hand it a MessageChannel
//      `port2`, mount the iframe, install history + WebSocket-shim
//      bridges.
//
// Apex hostnames (no app label) are edge-served listing pages — the
// bootstrap bails before step 1.
//
// Every stage is exported so the edge renderer and tests can drive
// them piecewise.

import { parseHostname } from "./url.js";
import { decode as zbase32Decode } from "./zbase32.js";
import {
  connectToBox,
  DtlsFingerprintMismatchError,
  WebcryptoUnsupportedError,
  WebrtcHandshakeTimeoutError,
  type PeerConnectResult,
} from "./webrtc/connection.js";
import {
  startRelayUpgradeMonitor,
  type RelayUpgradeMonitor,
} from "./webrtc/relay-upgrade.js";
import { verifyAliasBinding, decodeBase64url } from "./webrtc/binding-verify.js";
import {
  installHistorySync,
  type IframeLike,
} from "./wrapper/history.js";
import { installWsBridge } from "./wrapper/ws-bridge.js";
import { sendInChunks } from "./wrapper/dc-chunk.js";
import {
  claimEarlySignal,
  discardEarlySignal,
  openEarlySignal,
  rememberSignalBase,
  safeGetLocalStorage,
} from "./wrapper/early-signal.js";
import { installBasicAuthPromptListener } from "./wrapper/basic-auth-prompt.js";
import { installAuthRequiredListener } from "./wrapper/auth-required-prompt.js";
import { renderInAppWebviewPage } from "./wrapper/in-app-webview.js";
import { installSwUpdatePoll } from "./wrapper/sw-update-poll.js";
import {
  captureEarlyWakes,
  installSwHandoff,
  type EarlyWakeCapture,
} from "./wrapper/sw-handoff.js";
import {
  ensureSwControllable,
  safeGetSessionStorage,
} from "./wrapper/sw-recovery.js";
import {
  openSession,
  type EstablishContext,
  type SessionStatus,
} from "./wrapper/session.js";
import type { ByteTransport } from "./translator/client.js";
import { pristineWrapperHtml } from "./wrapper-html.js";

/**
 * Compile-time-substituted pin of coord's alias-binding root public
 * key (base64url-encoded, 32 bytes). Defined by vite via `define:
 * __P2CLAW_ROOT_PUBKEY__`. Empty string at runtime means the build
 * had no env var — treat as a misconfigured bundle and refuse to
 * boot (see `getRootPubkey`).
 */
declare const __P2CLAW_ROOT_PUBKEY__: string;

export interface WrapperConfig {
  /** Operator-chosen apex, e.g. "p2claw.com". */
  parentDomain: string;
}

export interface BootTimingEntry {
  phase: string;
  atMs: number;
  sinceStartMs: number;
  deltaMs: number;
  detail?: string;
}

declare global {
  interface Window {
    __p2clawBootTimings?: BootTimingEntry[];
    __p2clawBootStartMs?: number;
    __p2clawMarkBootTiming?: (phase: string, detail?: string) => void;
  }
}

/** Response shape from `POST /v1/connect`. */
export interface ConnectResponse {
  session_id: string;
  /** z-base-32 encoded 32-byte peer_id. */
  peer_id: string;
  signal_url: string;
  signal_token: string;
  ice_servers?: RTCIceServer[];
  turn_expires_at?: number;
}

/**
 * End-to-end bootstrap. Called once at page load time.
 */
export async function bootWrapper(config: WrapperConfig): Promise<void> {
  // The page as served, for the SW's reload cache: taken before boot
  // changes anything (status text, overlays, the mounted iframe), and
  // cleaned in case this boot itself came from a mounted copy.
  const wrapperHtml = pristineWrapperHtml(
    "<!DOCTYPE html>\n" + document.documentElement.outerHTML,
  );
  const timing = createBootTimer();
  timing.mark(
    "start",
    `host=${window.location.hostname} path=${window.location.pathname}${window.location.search}`,
  );
  const parsed = parseHostname(window.location.hostname, config.parentDomain);
  if (!parsed.ok) {
    timing.mark("hostname_failed", `reason=${parsed.reason}`);
    renderStatus(`hostname parse failed: ${parsed.reason}`, "error");
    return;
  }
  timing.mark(
    "hostname_ok",
    `alias=${parsed.value.aliasLabel} app=${parsed.value.appLabel ?? "none"}`,
  );
  // Apex-form hostnames (e.g. `blue-otter-7392.p2claw.com` with no
  // app label) are edge-served listing pages, not peer URLs. The
  // bootstrap should not attempt a handshake there — the edge is
  // responsible for rendering the listing HTML. If we somehow get
  // included on an apex page, bail cleanly.
  if (parsed.value.appLabel === null) {
    timing.mark("apex_skip");
    renderStatus("apex: nothing to bootstrap (edge serves this page)", "info");
    return;
  }

  // iOS in-app WKWebViews (X, Instagram, Facebook, Line, TikTok,
  // Snapchat, Slack, …) strip `navigator.serviceWorker`. The SW is
  // a hard requirement, so render an "Open in Browser" interstitial
  // instead of throwing. Android in-app browsers are Chrome-backed
  // and have SW, so they pass through normally.
  if (!("serviceWorker" in navigator)) {
    timing.mark("in_app_webview_intercept");
    renderInAppWebviewPage();
    return;
  }

  // Read edge-injected meta tags (peer_id, alias, binding signature).
  const meta = readMetaTags();
  timing.mark("meta_read", `alias=${meta.alias} app=${parsed.value.appLabel}`);

  // Verify the coord-signed alias→peer_id binding before trusting
  // any other meta-tag value. If native Ed25519 is unavailable
  // (Safari ≤ 16, Firefox ≤ 128, Chrome ≤ 112),
  // `verifyAliasBinding` falls back to a dynamic `@noble/ed25519`
  // import; `onFallbackStart` mounts an upgrade-hint overlay while
  // the chunk loads.
  const rootPubkey = getRootPubkey();
  const peerIdBytes = zbase32Decode(meta.peerId, 32);
  timing.mark("binding_verify_start");
  const bindingResult = await verifyAliasBinding({
    alias: meta.alias,
    peerIdBytes,
    issuedAt: meta.bindingIssuedAt,
    signatureB64url: meta.bindingSig,
    rootPubkeyBytes: rootPubkey,
    onFallbackStart: () => showFallbackOverlay(),
  });
  // Tear down the overlay regardless of outcome — if verification
  // succeeded via fallback, hide silently; if it failed, the next
  // renderStatus call will surface the actual error.
  hideFallbackOverlay();
  if (!bindingResult.ok) {
    timing.mark("binding_verify_failed", `reason=${bindingResult.reason}`);
    if (bindingResult.reason === "unsupported") {
      // Neither native Ed25519 nor the noble fallback could run.
      // Honest copy: it's a browser issue, not a MITM.
      renderStatus(
        "Your browser is too old to verify the connection's identity, and the compatibility fallback didn't load. Try Chrome 113+, Firefox 129+, or Safari 17+.",
        "error",
      );
      throw new Error(
        `webcrypto_unsupported: alias=${meta.alias} peer_id=${meta.peerId}`,
      );
    }
    // reason === "invalid": at least one verifier ran end-to-end and
    // rejected the signature. Old browsers are no longer in the
    // suspect list — this is a real binding_sig_invalid.
    renderStatus(
      "binding_sig_invalid: Address verification failed. Possible MITM or coord misconfiguration. Not connecting.",
      "error",
    );
    throw new Error(
      `binding_sig_invalid: alias=${meta.alias} peer_id=${meta.peerId}`,
    );
  }
  timing.mark("binding_verify_done", `via=${bindingResult.via}`);

  // Catch any `p2claw_sw_wake` broadcasts that fire while
  // `openSession` runs (hundreds of ms of /v1/connect + SDP + DTLS).
  // `installSwHandoff` takes ownership via `pendingWakes` once it's
  // ready.
  let earlyWakes: EarlyWakeCapture | undefined;
  if ("serviceWorker" in navigator) {
    earlyWakes = captureEarlyWakes(navigator.serviceWorker);
    timing.mark("early_wake_capture");
  }

  // Kick SW registration in parallel with the WebRTC handshake so
  // the SW fetch + activate overlap with /v1/connect and the data
  // channel come-up. Awaited later just before `installSwUpdatePoll`.
  // `.catch(() => undefined)` suppresses unhandledrejection on a
  // pre-await boot failure; the actual rejection still surfaces to
  // the awaiter below.
  let swRegisterPromise: Promise<ServiceWorkerRegistration> | null = null;
  if ("serviceWorker" in navigator) {
    timing.mark("sw_register_kicked");
    swRegisterPromise = registerServiceWorker();
    swRegisterPromise.catch(() => undefined);
  }

  // Tracks the relay→direct upgrade monitor for the *current* pc.
  // Ref-cell so the catch path can dispose a partially-initialised
  // monitor; TS narrows a bare `let` to `null` inside the async
  // `establish` closure and `?.dispose()` errors with `never`.
  const relayMonitorRef: { current: RelayUpgradeMonitor | null } = {
    current: null,
  };

  try {
    // Per-attempt establishment closure: re-runs for each reconnect
    // so session_id + ICE creds + DC are fresh.
    const iceTransportPolicy: RTCIceTransportPolicy = isForceRelay(
      window.location.search,
    )
      ? "relay"
      : "all";
    // Test seam: `?p2claw-relay-upgrade-poll-ms=N` overrides the
    // monitor's poll interval (default 30s). The e2e harness uses
    // 2000 ms so a give_up cycle finishes within budget.
    const relayUpgradePollMs = parseRelayUpgradePollMs(window.location.search);

    const establish = async (
      ctx: EstablishContext,
    ): Promise<PeerConnectResult> => {
      const kind = ctx.phase ? "reconnect" : "initial";
      const storage = safeGetLocalStorage();
      const early = openEarlySignal(storage);
      timing.mark("connect_request_start", `kind=${kind} early_signal=${early !== null}`);
      let resolved: ConnectResponse;
      try {
        resolved = await fetchConnectInfo({
          alias: meta.alias,
          app: parsed.value.appLabel,
        });
      } catch (e) {
        discardEarlySignal(early);
        timing.mark("connect_request_failed", `kind=${kind} ${errorDetail(e)}`);
        throw e;
      }
      rememberSignalBase(storage, resolved.signal_url);
      const preopenedSignal = claimEarlySignal(early, resolved.signal_url);
      timing.mark(
        "connect_response",
        `kind=${kind} session=${resolved.session_id} ice_servers=${resolved.ice_servers?.length ?? 0}`,
      );
      // Consistency check vs the binding — catches coord/edge drift
      // that would let a tampered edge pair a valid binding for
      // peer A with a /v1/connect payload pointing at peer B.
      if (resolved.peer_id.toLowerCase() !== meta.peerId.toLowerCase()) {
        preopenedSignal?.close();
        timing.mark("connect_peer_mismatch", `kind=${kind}`);
        throw new Error(
          `signature_mismatch: /v1/connect peer_id (${resolved.peer_id}) does not match binding (${meta.peerId})`,
        );
      }
      ctx.phase?.("connect_ok", `session=${resolved.session_id}`);
      timing.mark("webrtc_start", `kind=${kind} session=${resolved.session_id}`);
      let peer: PeerConnectResult;
      try {
        peer = await connectToBox({
          peerIdBytes,
          sessionId: resolved.session_id,
          signalUrl: resolved.signal_url,
          signalToken: resolved.signal_token,
          preopenedSignal,
          iceConfig: {
            iceServers: resolved.ice_servers ?? [],
            iceTransportPolicy,
          },
          onPcCreated: () => ctx.phase?.("pc_new"),
          onPhase: (phase, detail) =>
            timing.mark(
              `webrtc_${phase}`,
              detail
                ? `kind=${kind} session=${resolved.session_id} ${detail}`
                : `kind=${kind} session=${resolved.session_id}`,
            ),
          // Per-attempt abort signal so a hung handshake doesn't
          // wedge the reconnect loop.
          signal: ctx.signal,
        });
      } catch (e) {
        timing.mark(
          "webrtc_failed",
          `kind=${kind} session=${resolved.session_id} ${errorDetail(e)}`,
        );
        throw e;
      }
      ctx.phase?.("dc_open");
      timing.mark("webrtc_done", `kind=${kind} session=${resolved.session_id}`);
      // Relay → direct upgrade monitor. No-op when the ICE path is
      // already direct or when the session is force-relay.
      relayMonitorRef.current?.dispose();
      relayMonitorRef.current =
        iceTransportPolicy === "relay"
          ? null
          : startRelayUpgradeMonitor(peer.pc, {
              pollIntervalMs: relayUpgradePollMs,
            });
      return peer;
    };

    // `openSession` owns the pc/dc/translator for the tab lifetime
    // and transparently reconnects on mid-session drops via the
    // same `establish` closure.
    const session = await openSession({
      establish,
      makeTransport: dataChannelTransport,
      onStatus: (s) => renderSessionStatus(s),
      // pause retries while the tab is hidden, resume on
      // visible. Prevents the mobile-backgrounded case where the
      // OS suspends the tab, JS timers don't fire reliably, and a
      // reconnect loop running through its budget surfaces
      // `box_offline` the moment the user returns.
      isHidden: () => document.visibilityState === "hidden",
      awaitVisible: () =>
        new Promise<void>((resolve) => {
          if (document.visibilityState === "visible") return resolve();
          const listener = (): void => {
            if (document.visibilityState === "visible") {
              document.removeEventListener("visibilitychange", listener);
              resolve();
            }
          };
          document.addEventListener("visibilitychange", listener);
        }),
    });
    timing.mark("session_ready");

    // visibility-driven reconnect kick. On mobile (and
    // sometimes desktop) the DC silently dies during a long
    // background period without the `pc.connectionState=
    // disconnected` / `dc.close` events ever firing in a way the
    // page sees on resume. Force a fresh reconnect cycle when the
    // user returns to the tab AND we don't look alive. `pageshow`
    // covers the bfcache (back/forward navigation) restore path,
    // which doesn't fire `visibilitychange`.
    const onVisibilityCheck = (): void => {
      if (document.visibilityState !== "visible") return;
      if (session.isAlive()) return;
      session.kickReconnect("visibilitychange");
    };
    document.addEventListener("visibilitychange", onVisibilityCheck);
    window.addEventListener("pageshow", (ev) => {
      // bfcache restore: `persisted === true`. Treat the same as a
      // visibility-change to visible.
      if ((ev as PageTransitionEvent).persisted) onVisibilityCheck();
    });

    // (6) Register the SW and hand over its port. The handoff also
    // installs a `p2claw_sw_wake` listener that re-hands a fresh
    // MessageChannel if the SW cold-respawns later — no re-register
    // on the app's side is required.
    //
    // `swRegisterPromise` was kicked early (right after
    // `early_wake_capture`), so the SW download + activate has been
    // running concurrent with `openSession`. On a typical
    // cold-visitor flow, `register()` and `serviceWorker.ready` have
    // both already resolved by the time we await here, and this
    // becomes a microtask. The fallback `registerServiceWorker()`
    // call only fires on browsers without `serviceWorker` (where
    // the early kick was skipped) — same behavior as the original, the
    // throw inside `registerServiceWorker` surfaces "no SW support".
    timing.mark("sw_register_start");
    const swRegistration = await (swRegisterPromise ?? registerServiceWorker());
    timing.mark("sw_register_done");

    // periodic + visibility-driven `registration.update()`
    // poll. Without this, long-lived tabs stay on stale bundles
    // until manually reloaded (the browser's own `sw.js` byte-check
    // only fires on navigation or its own ~24h timer). Skip-waiting
    // + clients.claim already silently apply a new bundle the
    // moment the browser notices; we just have to TRIGGER the
    // notice. Boot one-shot + visibility-on-visible + periodic
    // 30 min while visible.
    installSwUpdatePoll({ registration: swRegistration });

    // SW-control recovery. Two cases land here: Ctrl+Shift+R bypass
    // (permanently uncontrolled navigation) and first-visit claim
    // race (SW activated but controller binding hasn't propagated).
    // Both would dead-end `installSwHandoff`'s `waitForController()`.
    // Recovery soft-reloads once (loop-guarded) so the next page
    // load enters with a controller set. On "uncontrolled" we
    // couldn't recover (no sessionStorage / SW never ready / loop
    // guard tripped); render a clear error and bail rather than
    // hanging on `installSwHandoff`.
    timing.mark("sw_recovery_start");
    const recovery = await ensureSwControllable({
      navigatorSw: navigator.serviceWorker,
      swReady: navigator.serviceWorker.ready,
      sessionStorage: safeGetSessionStorage(),
      reload: () => window.location.reload(),
    });
    timing.mark("sw_recovery_done", `kind=${recovery.kind}`);
    if (recovery.kind === "reloading") {
      // `window.location.reload()` has been called; the current
      // execution context is dead-ish. Don't dispose earlyWakes —
      // it lives on `navigator.serviceWorker` which gets torn down
      // with the page. Abort boot silently; the reload will start
      // fresh.
      timing.mark("sw_recovery_reload");
      return;
    }
    if (recovery.kind === "uncontrolled") {
      // `installSwHandoff` would hang on `waitForController` here.
      // Render a clear error instead of the silent blank-page we
      // used to ship on this path, then bail from boot. User can
      // refresh (non-hard) to recover — the sessionStorage flag
      // lets a future hard-reload try recovery again.
      console.warn(
        `[p2claw:wrapper] aborting boot: sw_uncontrolled (${recovery.reason})`,
      );
      timing.mark("sw_uncontrolled", `reason=${recovery.reason}`);
      earlyWakes?.dispose();
      earlyWakes = undefined;
      renderStatus(
        "Service worker not in control (try a regular refresh).",
        "error",
      );
      return;
    }

    timing.mark("sw_handoff_start");
    const swHandoff = await installSwHandoff({
      navigatorSw: navigator.serviceWorker,
      translator: session.translator,
      // The snapshot from the start of boot, not the live DOM: a
      // re-handoff (SW respawn, new bundle) runs after `mountIframe`.
      documentHtml: () => wrapperHtml,
      pendingWakes: earlyWakes,
    });
    timing.mark("sw_handoff_done");
    // `installSwHandoff` has taken over wake duty and disposed the
    // pre-listener internally; drop our reference so dispose is
    // truly one-shot in a failure path.
    earlyWakes = undefined;

    // Firefox / older-Safari fallback shuttle. Feature-gated on
    // `cookieStore` ABSENCE — Chromium / Safari TP have cookieStore
    // and this block is a complete no-op there. Without this,
    // Firefox can't see the session cookie inside the SW (cookieStore
    // is the only SW-side cookie API; document.cookie doesn't exist
    // in workers; FetchEvent.request.headers strips Cookie). The
    // wrapper reads document.cookie + pushes the value over the
    // wrapper port. SW caches it. `readSessionCookie` returns the
    // cache when cookieStore.get is unavailable.
    if (typeof globalThis.cookieStore === "undefined") {
      installSessionCookieShuttle(swHandoff, window);
    }

    // (7) Install SW-message listeners BEFORE mounting the iframe.
    // The iframe's nav fetch fires the moment its `src` is set; the
    // SW intercepts, forwards to the box, and can post
    // `p2claw_auth_required` (or `p2claw_basic_auth_needed`) faster
    // than the wrapper would otherwise install its listener. On a
    // warm-DC case (post-logout reload) the 401 fires in single-
    // digit milliseconds and the message is lost — the picker
    // never appears. Order-of-install matters; the listeners are
    // pure event-handlers with no port dependency, so they're
    // safe to install before the iframe exists.
    //
    // OAuth `auth-required` redirector. SW pings on the
    // daemon middleware's `401 + P2claw-Auth-Required: true`.
    // Unlike Basic Auth, OAuth navigates the TOP window through the
    // broker — listener navigates `window.location` rather than
    // mutating `iframe.src`. Post-sign-in round-trips through
    // `/__p2claw/oauth/land` (edge) which sets the
    // `__p2claw_session` cookie and 303s back to `returnUrl`.
    installAuthRequiredListener({
      navigatorSw: navigator.serviceWorker,
      appLabel: parsed.value.appLabel,
      aliasLabel: meta.alias,
      peerIdZ32: meta.peerId,
    });

    // (8) Mount the iframe, install iframe-bound subsystems.
    const iframe = mountIframe(() => timing.mark("iframe_load"));
    timing.mark("iframe_mounted");
    installHistorySync({
      iframe: iframe as unknown as IframeLike,
      window: window as Window & typeof globalThis,
      initialPath:
        window.location.pathname + window.location.search + window.location.hash,
    });
    installWsBridge({
      iframe,
      translator: session.translator,
      window: window as Window & typeof globalThis,
      // Cookie the WS handshake from the SW's jar ⊕ cookieStore so
      // cookie-authenticated WebSockets ride the app's session.
      getCookieHeader: (url) => swHandoff.getCookieHeader(url),
    });
    // SPA-style 401-Basic prompt (task): SW pings us via
    // `p2claw_basic_auth_needed` whenever the box returns 401-Basic
    // on a non-nav fetch. Listener needs the iframe so it can
    // navigate `iframe.src` to the login form — must be installed
    // AFTER mountIframe, but that's fine because the burst-dedup
    // inside the listener tolerates a missed first ping (subsequent
    // 401s re-fire the message).
    installBasicAuthPromptListener({
      navigatorSw: navigator.serviceWorker,
      iframe,
    });
    timing.mark("boot_done");
  } catch (e) {
    timing.mark("boot_failed", errorDetail(e));
    // Boot failed before `installSwHandoff` took ownership of the
    // early-wake listener — tear it down ourselves so it doesn't
    // leak on the navigator across a reload / error-page state.
    earlyWakes?.dispose();
    // The relay-upgrade monitor (if armed) holds a pc state listener
    // and a pending poll timer; dispose so a half-booted page
    // showing the error overlay doesn't keep polling getStats() in
    // the background.
    relayMonitorRef.current?.dispose();
    if (e instanceof ConnectError) {
      // Terminal per-peer state — render the styled full-page template
      // instead of leaking the raw HTTP status to `#p2claw-status`.
      // The user hasn't had a working session yet, so there's nothing
      // to reconnect to; only a refresh will retry.
      console.error(`[p2claw:wrapper] /v1/connect terminal: ${e.kind}`, e);
      showBoxErrorPage(e.kind);
      throw e;
    }
    console.error("[p2claw:wrapper] fatal during boot:", e);
    renderStatus(friendlyBootErrorMessage(e), "error");
    throw e;
  }
}

/**
 * map a fatal-boot exception to user-facing copy. Goal: replace
 * raw error message strings (which leak internal terminology like
 * "RTCPeerConnection: state=failed" / "DataChannel: closed before
 * open") with something a user can act on.
 *
 * Matchers run from most-specific to most-generic. The catch-all
 * preserves the previous behavior of surfacing the raw message so any
 * unrecognized error still tells an operator what went wrong.
 *
 * Exported for unit tests only — production callers use it internally
 * via the boot catch block.
 */
export function friendlyBootErrorMessage(e: unknown): string {
  // Honest "browser too old" copy — already actionable, keep as-is.
  // Same string the binding-verify path renders.
  if (e instanceof WebcryptoUnsupportedError) return e.message;

  // Identity-mismatch is a security-relevant signal; we don't soften
  // it. The wrapper already renders the scary copy on the binding-
  // verify path; if it gets surfaced via the DTLS-fp path here too,
  // route through a single "address verification failed" string so
  // the user sees the same warning for both layers.
  if (e instanceof DtlsFingerprintMismatchError) {
    return "binding_sig_invalid: Address verification failed. Possible MITM or coord misconfiguration. Not connecting.";
  }

  // Silent-hang case: WebRTC stack stalled and never reported failure.
  // Single actionable string. Detail (last phase) is already in the
  // logged error for operator triage; users see only the concise UI
  // copy.
  if (e instanceof WebrtcHandshakeTimeoutError) {
    return "Could not connect to the box. Try refreshing, or use a different browser if the problem persists.";
  }

  // Generic WebRTC failure paths (state=failed, ICE failed, DC
  // closed/errored before open). Same actionable copy — we don't try
  // to distinguish causes for the user.
  if (e instanceof Error) {
    const msg = e.message;
    if (
      /^RTCPeerConnection: /.test(msg) ||
      /^DataChannel: /.test(msg)
    ) {
      return "Could not connect to the box. Try refreshing, or use a different browser if the problem persists.";
    }
    return msg;
  }
  return String(e);
}

// ---------- boot timing ----------

function createBootTimer(): {
  mark(phase: string, detail?: string): void;
} {
  if (typeof window.__p2clawMarkBootTiming === "function") {
    return { mark: window.__p2clawMarkBootTiming };
  }
  const entries: BootTimingEntry[] = window.__p2clawBootTimings ?? [];
  const startMs = readNowMs();
  let lastMs = startMs;
  window.__p2clawBootStartMs = startMs;

  const mark = (phase: string, detail?: string): void => {
    const now = readNowMs();
    const entry: BootTimingEntry = {
      phase,
      atMs: roundTimingMs(now),
      sinceStartMs: roundTimingMs(now - startMs),
      deltaMs: roundTimingMs(now - lastMs),
    };
    if (detail) entry.detail = detail;
    entries.push(entry);
    lastMs = now;
    window.__p2clawBootTimings = entries;
    try {
      globalThis.performance?.mark?.(`p2claw:boot:${phase}`);
    } catch {
      /* diagnostic only */
    }
    const timing = `t=${formatTimingMs(entry.sinceStartMs)} +${formatTimingMs(entry.deltaMs)} nav=${formatTimingMs(entry.atMs)}`;
    if (detail) {
      console.info(`p2claw:boot:${phase}`, `${timing} ${detail}`);
    } else {
      console.info(`p2claw:boot:${phase}`, timing);
    }
  };
  window.__p2clawMarkBootTiming = mark;
  return { mark };
}

function readNowMs(): number {
  return globalThis.performance?.now?.() ?? Date.now();
}

function roundTimingMs(value: number): number {
  return Math.round(value * 10) / 10;
}

function formatTimingMs(value: number): string {
  return `${value.toFixed(1)}ms`;
}

function errorDetail(e: unknown): string {
  if (e instanceof Error) {
    return `error=${e.name}:${e.message}`;
  }
  return `error=${String(e)}`;
}

// ---------- meta tag reader ----------

export interface MetaTags {
  peerId: string;
  alias: string;
  /** base64url-encoded 64-byte Ed25519 signature (no padding). */
  bindingSig: string;
  /** Unix seconds the binding was issued (coord root clock). */
  bindingIssuedAt: number;
}

export function readMetaTags(doc: Document = document): MetaTags {
  const get = (name: string): string => {
    const el = doc.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    const c = el?.content?.trim();
    if (!c) {
      throw new Error(`missing <meta name="${name}"> in wrapper HTML`);
    }
    return c;
  };
  const issuedAtStr = get("p2claw-binding-issued-at");
  const issuedAt = Number.parseInt(issuedAtStr, 10);
  if (!Number.isFinite(issuedAt) || issuedAt < 0 || String(issuedAt) !== issuedAtStr) {
    throw new Error(
      `invalid <meta name="p2claw-binding-issued-at"> content: ${JSON.stringify(issuedAtStr)}`,
    );
  }
  return {
    peerId: get("p2claw-peer-id"),
    alias: get("p2claw-alias"),
    bindingSig: get("p2claw-binding-sig"),
    bindingIssuedAt: issuedAt,
  };
}

// ---------- root pubkey pin ----------

/**
 * Resolve and decode the pinned coord-root Ed25519 public key into 32
 * raw bytes. The constant is substituted at build time by vite
 * (`__P2CLAW_ROOT_PUBKEY__`) from the `VITE_P2CLAW_ROOT_PUBKEY` env
 * var. An empty string means the bundle was built without the env
 * var set — that's a misconfigured deploy, not something to
 * paper over. Bail hard so a broken build is loud, not silently
 * insecure (we'd otherwise accept any binding signature).
 */
function getRootPubkey(): Uint8Array {
  const encoded = __P2CLAW_ROOT_PUBKEY__;
  if (!encoded) {
    throw new Error(
      "bootstrap built without VITE_P2CLAW_ROOT_PUBKEY — alias binding cannot be verified; refusing to boot.",
    );
  }
  const bytes = decodeBase64url(encoded);
  if (!bytes || bytes.length !== 32) {
    throw new Error(
      `bootstrap root pubkey is malformed (expected 32 bytes base64url, got ${bytes?.length ?? "invalid encoding"})`,
    );
  }
  return bytes;
}

// ---------- /v1/connect ----------

export async function fetchConnectInfo(input: {
  alias: string;
  app: string | null;
}): Promise<ConnectResponse> {
  const body: Record<string, unknown> = {
    alias: input.alias,
    visitor_kind: "browser",
  };
  if (input.app) body.app = input.app;

  const res = await fetch("/v1/connect", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.ok) return (await res.json()) as ConnectResponse;
  // Error path: read body once and classify. The three terminal
  // states (404 no_such_peer, 410 revoked, 503 box_offline) get a
  // typed `ConnectError` so `bootWrapper` can render a styled
  // full-page template instead of surfacing the raw HTTP error.
  // Anything else becomes a generic fetch failure.
  const rawBody = await res.text().catch(() => "");
  const kind = classifyConnectError(res.status, rawBody);
  if (kind) throw new ConnectError(kind, res.status);
  throw new Error(`/v1/connect failed: HTTP ${res.status} ${rawBody}`);
}

// ---------- terminal /v1/connect errors ----------

/** Discriminator for the three terminal `/v1/connect` states
 *  enumerated in  Anything outside this set is
 *  treated as a generic fetch failure. */
export type ConnectErrorKind = "no_such_peer" | "revoked" | "box_offline";

/**
 * Thrown by `fetchConnectInfo` when coord reports a terminal error.
 * Carries the semantic `kind` so the caller can pick the right
 * full-page template without re-inspecting HTTP status.
 */
export class ConnectError extends Error {
  readonly kind: ConnectErrorKind;
  readonly httpStatus: number;
  constructor(kind: ConnectErrorKind, httpStatus: number) {
    super(`/v1/connect terminal: ${kind} (HTTP ${httpStatus})`);
    this.name = "ConnectError";
    this.kind = kind;
    this.httpStatus = httpStatus;
  }
}

function isConnectErrorKind(s: string): s is ConnectErrorKind {
  return s === "no_such_peer" || s === "revoked" || s === "box_offline";
}

/**
 * Classify a `/v1/connect` error response. Prefers the JSON body's
 * `error` field when present (coord emits
 * `{"error": "<kind>"}`), falls back to mapping by HTTP status for
 * the three documented terminal codes. Returns `null` for anything
 * else — that's a generic 5xx / transport failure, not a terminal
 * per-peer state. Exported for tests.
 */
export function classifyConnectError(
  status: number,
  body: string,
): ConnectErrorKind | null {
  if (body) {
    try {
      const j = JSON.parse(body) as { error?: unknown };
      if (typeof j.error === "string" && isConnectErrorKind(j.error)) {
        return j.error;
      }
    } catch {
      /* not JSON — fall through to status-based mapping */
    }
  }
  switch (status) {
    case 404:
      return "no_such_peer";
    case 410:
      return "revoked";
    case 503:
      return "box_offline";
    default:
      return null;
  }
}

// ---------- data channel transport ----------

/** Resume parked sends once `dc.bufferedAmount` drains below this
 *  (`bufferedAmountLowThreshold`). */
const DC_SEND_RESUME_BYTES = 1024 * 1024;

/** High-water mark on `dc.bufferedAmount`. Past it, `send` parks
 *  until `bufferedamountlow` instead of piling more onto the SCTP
 *  buffer — the browser hard-closes the channel when its internal
 *  buffering limit is exceeded, and unbounded buffering head-of-line
 *  blocks every other stream on the channel. Below the mark, sends
 *  stay synchronous. */
const DC_SEND_HIGH_WATER_BYTES = 4 * 1024 * 1024;

/**
 * Adapt an RTCDataChannel to the `ByteTransport` contract the
 * translator client expects. Backpressure rides the returned
 * promise: windowed (not stop-and-wait) via the high-water mark
 * above.
 */
export function dataChannelTransport(session: PeerConnectResult): ByteTransport {
  const { dataChannel: dc } = session;
  dc.binaryType = "arraybuffer";
  dc.bufferedAmountLowThreshold = DC_SEND_RESUME_BYTES;

  const waitForDrain = (): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      const cleanup = (): void => {
        dc.removeEventListener("bufferedamountlow", onLow);
        dc.removeEventListener("close", onClose);
      };
      const onLow = (): void => {
        cleanup();
        resolve();
      };
      const onClose = (): void => {
        cleanup();
        reject(new Error("data channel closed while awaiting send drain"));
      };
      dc.addEventListener("bufferedamountlow", onLow);
      dc.addEventListener("close", onClose);
    });

  // Sends that hit the high-water mark queue FIFO behind `sendTail`
  // so a later frame can't slip past one parked on drain (whole
  // frames may interleave across streams, but a caller that awaits
  // send() must observe its own ordering).
  let queuedSends = 0;
  let sendTail: Promise<void> = Promise.resolve();

  return {
    send(bytes) {
      // RTCDataChannel.send requires a plain-ArrayBuffer-backed
      // BufferSource. Copy to shed any `SharedArrayBuffer` annotation
      // the caller may have.
      const buf = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(buf).set(bytes);
      if (queuedSends === 0 && dc.bufferedAmount <= DC_SEND_HIGH_WATER_BYTES) {
        sendInChunks(dc, buf);
        return;
      }
      queuedSends += 1;
      const send = sendTail.then(async () => {
        while (dc.bufferedAmount > DC_SEND_HIGH_WATER_BYTES) {
          if (dc.readyState !== "open") {
            throw new Error(
              `data channel not open while awaiting send drain (state=${dc.readyState})`,
            );
          }
          await waitForDrain();
        }
        sendInChunks(dc, buf);
      });
      sendTail = send.then(
        () => {
          queuedSends -= 1;
        },
        () => {
          queuedSends -= 1;
        },
      );
      return send;
    },
    onMessage(listener) {
      const handler = (ev: MessageEvent) => {
        const data = ev.data;
        if (data instanceof ArrayBuffer) {
          listener(new Uint8Array(data));
        } else if (data instanceof Uint8Array) {
          listener(data);
        }
      };
      dc.addEventListener("message", handler);
      return () => dc.removeEventListener("message", handler);
    },
    onClose(listener) {
      dc.addEventListener("close", listener);
      return () => dc.removeEventListener("close", listener);
    },
  };
}

// ---------- SW registration ----------

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("browser has no Service Worker support");
  }
  // `type: "module"` so the SW bundle can use ES imports (its vite
  // build emits shared code into `/_p2claw/chunks/`). Requires Chrome
  // ≥ 103 / Firefox ≥ 114 / Safari ≥ 15.4 — all already implied by the
  // Ed25519-via-WebCrypto baseline.
  const reg = await navigator.serviceWorker.register("/_p2claw/sw.js", {
    scope: "/",
    type: "module",
    // Never satisfy the SW script (or its imports) from the HTTP cache
    // on an update check — always hit the network. Belt-and-suspenders
    // with the edge's `no-cache` on sw.js: guarantees a deploy's new SW
    // is seen instead of the browser comparing against stale cached
    // bytes and keeping the old worker.
    updateViaCache: "none",
  });
  await navigator.serviceWorker.ready;
  return reg;
}

// ---------- iframe ----------

/**
 * Push the current `__p2claw_session` cookie to the SW for Firefox /
 * older-Safari where the SW can't read cookieStore. Called ONLY when
 * `cookieStore` is missing in the wrapper context (the caller
 * feature-detects). On Chromium this whole function is unreachable.
 *
 * Pushes are sent over the wrapper-side MessagePort (the same channel
 * that delivers `fetch_response_head` and friends — handled by the
 * SW core's `handleWrapperMessage`). The earlier attempt at this
 * feature used `controller.postMessage`, which routes to the SW's
 * GLOBAL `self.addEventListener("message", ...)` in sw.ts — a
 * different channel that doesn't reach core's switch. That bug
 * silently lost every push; this implementation goes through the
 * port that `installSwHandoff` already manages.
 *
 * Three triggers for pushes:
 *  - Right after handoff (initial state)
 *  - `visibilitychange` to visible (catch cross-tab cookie changes
 *    while this tab was backgrounded)
 *  - A `controllerchange`-triggered handoff (new port → new SW
 *    instance with empty cache; re-push immediately)
 *
 * No clear-cookie path here — logout is handled by the SW's
 * `cookieStore.delete` on browsers that have cookieStore, and by
 * a separate document.cookie-clear path that lives outside this
 * shuttle (deferred to a follow-up).
 */
function installSessionCookieShuttle(
  swHandoff: { currentPort(): { postMessage(msg: unknown): void } | null },
  win: Window & typeof globalThis,
): void {
  const SESSION_COOKIE_NAME = "__p2claw_session";

  function readFromDocument(): string | null {
    // `document.cookie` is `name1=value1; name2=value2; …`. Naive
    // parse — we control both ends so RFC-strict tokenisation isn't
    // needed.
    const all = win.document.cookie ?? "";
    for (const raw of all.split(";")) {
      const part = raw.trim();
      const eq = part.indexOf("=");
      if (eq < 0) continue;
      if (part.slice(0, eq) !== SESSION_COOKIE_NAME) continue;
      return part.slice(eq + 1);
    }
    return null;
  }

  function pushToSw(): void {
    const port = swHandoff.currentPort();
    if (!port) return;
    const value = readFromDocument();
    try {
      port.postMessage({
        type: "session_cookie_value",
        v: 1,
        value,
      });
    } catch {
      // Port might be mid-replacement; the next push catches up.
    }
  }

  // Initial push immediately after handoff.
  pushToSw();
  // Re-push on tab focus changes — a sign-in in another tab on the
  // same origin only reaches this SW if we re-read document.cookie.
  win.addEventListener("visibilitychange", () => {
    if (win.document.visibilityState === "visible") pushToSw();
  });
}

function mountIframe(onLoad?: () => void): HTMLIFrameElement {
  const iframe = document.getElementById("p2claw-app");
  if (!(iframe instanceof HTMLIFrameElement)) {
    throw new Error("missing <iframe id=\"p2claw-app\"> in wrapper HTML");
  }
  if (onLoad) iframe.addEventListener("load", onLoad, { once: true });
  iframe.src = window.location.pathname + window.location.search;
  iframe.hidden = false;
  const status = document.getElementById("p2claw-status");
  if (status) status.textContent = "";
  return iframe;
}

function renderStatus(msg: string, kind: "info" | "error" = "info"): void {
  const el = document.getElementById("p2claw-status");
  if (!el) return;
  el.textContent = msg;
  el.dataset["kind"] = kind;
}

/**
 * React to session-level transitions. `connecting` / `reconnecting` /
 * `connected` are all transparent — the iframe stays rendered, so the
 * app keeps its DOM state. `reconnecting` also raises a non-blocking
 * "Reconnecting…" overlay so the user knows the box dropped mid-session;
 * the overlay tears down silently on reconnect ().
 * `offline` (backoff exhausted at the 5-minute wall) hides the iframe and
 * surfaces `box_offline`.
 */
function renderSessionStatus(status: SessionStatus): void {
  const iframe = document.getElementById("p2claw-app");
  switch (status) {
    case "connecting":
      // Initial connect: boot UI already shows status; leave it alone.
      return;
    case "connected":
      renderStatus("", "info");
      hideReconnectOverlay();
      if (iframe instanceof HTMLIFrameElement) iframe.hidden = false;
      return;
    case "reconnecting":
      // Transparent to the app; don't stomp on a running iframe.
      showReconnectOverlay();
      return;
    case "offline":
      hideReconnectOverlay();
      // Session-level exhaustion at the 5-minute wall. Reuse the same
      // full-page template the initial-connect path uses for
      // `/v1/connect` failures — mid-session and initial-connect
      // `box_offline` converge on one UX surface.
      showBoxErrorPage("box_offline");
      return;
  }
}

// ---------- reconnect overlay ----------

/** DOM id used for the non-blocking "Reconnecting…" banner. Exported
 *  for tests and for anything that wants to feature-detect the overlay. */
export const RECONNECT_OVERLAY_ID = "p2claw-reconnect-overlay";

/**
 * Insert (or update) the reconnect overlay into `document.body`. The
 * element is absolute-positioned, semi-transparent, and
 * `pointer-events: none` so it doesn't block clicks into the iframe.
 * Called on every `"reconnecting"` transition — idempotent.
 */
export function showReconnectOverlay(doc: Document = document): void {
  let el = doc.getElementById(RECONNECT_OVERLAY_ID);
  if (!el) {
    el = doc.createElement("div");
    el.id = RECONNECT_OVERLAY_ID;
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    // Inline styles so the overlay survives the app iframe's own CSS
    // and we don't depend on a global stylesheet being present.
    el.style.cssText = [
      "position:fixed",
      "top:0",
      "left:0",
      "right:0",
      "padding:6px 12px",
      "font:14px/1.4 system-ui,-apple-system,sans-serif",
      "color:#222",
      "background:rgba(252,226,168,0.92)",
      "border-bottom:1px solid rgba(0,0,0,0.1)",
      "text-align:center",
      "z-index:2147483647",
      "pointer-events:none",
    ].join(";");
    el.textContent = "Reconnecting…";
    doc.body.appendChild(el);
  }
}

/** Remove the reconnect overlay if present. No-op otherwise. */
export function hideReconnectOverlay(doc: Document = document): void {
  const el = doc.getElementById(RECONNECT_OVERLAY_ID);
  el?.remove();
}

// ---------- Ed25519 fallback overlay ----------

/** DOM id for the "Loading compatibility fallback…" overlay shown
 *  while we dynamic-import noble for browsers without native
 *  Ed25519. Exported for tests + harness feature-detection. */
export const FALLBACK_OVERLAY_ID = "p2claw-fallback-overlay";

/** Render the upgrade-hint overlay during the noble fallback load
 * . Idempotent — safe to call multiple times. Inline-styled
 *  so it survives without any global stylesheet, matching the
 *  reconnect overlay's belt-and-braces approach. */
export function showFallbackOverlay(doc: Document = document): void {
  let el = doc.getElementById(FALLBACK_OVERLAY_ID);
  if (!el) {
    el = doc.createElement("div");
    el.id = FALLBACK_OVERLAY_ID;
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.style.cssText = [
      "position:fixed",
      "top:0",
      "left:0",
      "right:0",
      "padding:10px 16px",
      "font:14px/1.4 system-ui,-apple-system,sans-serif",
      "color:#222",
      "background:rgba(252,226,168,0.95)",
      "border-bottom:1px solid rgba(0,0,0,0.1)",
      "text-align:center",
      "z-index:2147483647",
    ].join(";");
    el.innerHTML =
      '<strong>Loading compatibility fallback…</strong><br>' +
      '<small style="opacity:0.75">' +
      "Your browser doesn't support modern signature verification. " +
      "For better performance and security, upgrade to Chrome, Firefox, or Safari 17+." +
      "</small>";
    doc.body.appendChild(el);
  }
}

/** Tear down the fallback overlay if present. No-op otherwise. */
export function hideFallbackOverlay(doc: Document = document): void {
  const el = doc.getElementById(FALLBACK_OVERLAY_ID);
  el?.remove();
}

// ---------- force-relay flag ----------

/**
 * Detect the `?p2claw-force-relay=1` test hook on the current URL.
 * When present, the wrapper passes `iceTransportPolicy: "relay"` into
 * the RTCPeerConnection so the browser will ONLY consider TURN
 * candidates — used by Playwright's TURN coverage runs. Any other
 * value (including absent) falls back to the default `"all"` policy.
 *
 * Exported for tests — takes the query string rather than reading
 * `window.location` directly so it's drivable without a DOM.
 */
export function isForceRelay(search: string): boolean {
  try {
    const params = new URLSearchParams(search);
    return params.get("p2claw-force-relay") === "1";
  } catch {
    return false;
  }
}

/**
 * Parse the `?p2claw-relay-upgrade-poll-ms=N` test hook into a
 * positive integer. Returns `undefined` (the monitor's default of
 * 30 s) when the param is absent, malformed, zero, or negative.
 *
 * Exists as a test seam for the docker-NAT e2e suite — the monitor
 * polls every 30 s in production, which is too slow for a Playwright
 * test budget. The harness sets `?p2claw-relay-upgrade-poll-ms=2000`
 * so a full 3-attempt give_up cycle (or a `trigger → upgraded` round)
 * fits comfortably in a single test. Production builds with no query
 * param ignore the knob entirely — same shape as `?p2claw-force-relay`.
 */
export function parseRelayUpgradePollMs(search: string): number | undefined {
  try {
    const raw = new URLSearchParams(search).get("p2claw-relay-upgrade-poll-ms");
    if (raw === null) return undefined;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0 || String(n) !== raw) return undefined;
    return n;
  } catch {
    return undefined;
  }
}

// ---------- terminal full-page error template ----------

/** DOM id for the full-viewport error page. Exported for tests and
 *  for any harness that wants to feature-detect whether a terminal
 *  state has been rendered. */
export const ERROR_PAGE_ID = "p2claw-error-page";

/** Copy surfaced per terminal state. Exported for tests and for any
 *  operator that wants to diff intended copy vs. shipped copy. The
 *  strings match edge's offline / revoked / 404 pages
 *  so the visual story stays consistent whether
 *  coord answers via /v1/connect (bootstrap-side, this template) or
 *  the edge bounces the request pre-bootstrap (edge-side static). */
export interface BoxErrorCopy {
  readonly title: string;
  readonly body: string;
}

export function getBoxErrorCopy(kind: ConnectErrorKind): BoxErrorCopy {
  switch (kind) {
    case "no_such_peer":
      return {
        title: "Address not found",
        body: "Not a p2claw address.",
      };
    case "revoked":
      return {
        title: "Access revoked",
        body: "This peer has been revoked.",
      };
    case "box_offline":
      return {
        title: "Box offline",
        body: "This p2claw box is offline. Try again later.",
      };
  }
}

/**
 * Render the full-page terminal error template into `doc.body`. The
 * page is a fixed-viewport overlay with an opaque background — it
 * covers the wrapper UI (any `#p2claw-app` iframe and
 * `#p2claw-status` div) without actually detaching them, so the boot
 * DOM remains inspectable for diagnostics.
 *
 * Called from two sites:
 *   1. `bootWrapper`'s catch path when coord returns a typed
 *      `ConnectError` from `/v1/connect` (initial-connect terminal
 *      state — refresh is the only path forward for the user).
 *   2. `renderSessionStatus("offline")` when the mid-session
 *      reconnect loop exhausts its 5-minute wall (session-level
 *      `box_offline`).
 *
 * Idempotent — a second call with the same kind is a no-op; a call
 * with a different kind updates the rendered copy in place.
 */
export function showBoxErrorPage(
  kind: ConnectErrorKind,
  doc: Document = document,
): void {
  // Hide the iframe and clear the status line so the visible chrome
  // is fully owned by the error page. Duck-typed assignment so this
  // works against both the real `HTMLIFrameElement` and a minimal
  // fake document (no `instanceof HTMLIFrameElement` — that binding
  // doesn't exist in a non-DOM test environment).
  const iframe = doc.getElementById("p2claw-app");
  if (iframe) (iframe as unknown as { hidden: boolean }).hidden = true;
  const statusEl = doc.getElementById("p2claw-status");
  if (statusEl) statusEl.textContent = "";

  // Remove any previous instance rather than mutating in place — the
  // element is cheap and replace-all is simpler than diffing children.
  doc.getElementById(ERROR_PAGE_ID)?.remove();

  const copy = getBoxErrorCopy(kind);
  const page = doc.createElement("div");
  page.id = ERROR_PAGE_ID;
  page.setAttribute("role", "alert");
  page.setAttribute("data-kind", kind);
  // Inline styles so the page works without a stylesheet. Opaque
  // white background, system font, comfortable reading width.
  page.style.cssText = [
    "position:fixed",
    "inset:0",
    "background:#fff",
    "color:#333",
    "font:14px/1.5 system-ui,-apple-system,sans-serif",
    "padding:4em 1em",
    "z-index:2147483646",
    "overflow:auto",
  ].join(";");

  const inner = doc.createElement("div");
  inner.style.cssText = "max-width:36em;margin:0 auto";

  const h1 = doc.createElement("h1");
  h1.textContent = copy.title;
  h1.style.cssText =
    "font:600 1.25em/1.3 system-ui,-apple-system,sans-serif;margin:0 0 .5em";
  inner.appendChild(h1);

  const p = doc.createElement("p");
  p.textContent = copy.body;
  p.style.cssText = "margin:0 0 1em";
  inner.appendChild(p);

  const small = doc.createElement("small");
  small.textContent =
    "If you believe this is wrong, check the address or contact the box operator.";
  small.style.cssText = "color:#888";
  inner.appendChild(small);

  page.appendChild(inner);
  doc.body.appendChild(page);
}
